import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { overlayApi } from "@/lib/navBridge";
import { mediaUrl } from "@/lib/note";
import { usePhone } from "@/lib/usePhone";

import {
  canPan,
  clampPan,
  clampScale,
  fitScale,
  pinch,
  toggleScale,
  wheelFactor,
  zoomAbout,
  zoomLabel,
  type Size,
  type ZoomState,
} from "./lightboxZoom";

/**
 * The one modal in the app.
 *
 * Everything else here refuses to be one on purpose — the radical picker takes the dock rather than
 * a dialog so the sentence you are fixing stays on screen. This is the exception, and the reason is
 * the same rule read the other way: the thumbnails are 148x64 and 74x42, small enough that a VN
 * screenshot in one is a smudge. Expanding it is the whole interaction, there is nothing to keep
 * beside it, and you are done immediately.
 *
 * It takes a **filename**, not a URL, so `mediaUrl` stays the single place a media path is built.
 *
 * ## Zoom and pan (日本-350)
 *
 * Added for Review, where the picture is often a diagram or a line of dialogue in a screenshot and
 * "bigger" is not the same as "readable". It is given to **every** surface rather than to Review
 * alone (Alex, 16 Aug 2026): this is one shared component, so scoping it would mean two.
 *
 * **The close rule changed, and that is a reversal worth recording rather than a tidy-up.** This
 * file used to say, at the image: *"No stopPropagation on the image: 'click anywhere to close'
 * should be literal, and there is nothing in here to interact with."* Both halves stopped being
 * true the moment the image became draggable — a pan gesture ends in a click on the image, so a
 * literal close rule would make the picture impossible to move. The rule is now:
 *
 * - the **scrim** closes,
 * - the **image** does not — it pans,
 * - a **drag** never closes, whatever it ends on,
 * - **double-click** toggles fit ⇄ 原寸,
 * - Esc and Back close.
 *
 * **It opens at fit, not at 100%.** Most card images are smaller than the viewport, so `原寸` is
 * usually a way to make the image *smaller* — and the toolbar prints the true percentage rather
 * than calling fit 100%, which would be a comfortable lie about the only number on screen.
 */
interface LightboxApi {
  open: (filename: string) => void;
  close: () => void;
}

const Context = createContext<LightboxApi | null>(null);

export function useLightbox(): LightboxApi {
  const api = useContext(Context);
  if (!api) throw new Error("useLightbox needs a LightboxProvider above it");
  return api;
}

/** Room left around the image at fit, so it does not touch the edges of the screen. */
const FIT_PADDING = 48;

/** How far a pointer may travel between press and release and still count as a click.
 *
 *  A few pixels rather than zero, because a mouse drifts during a press and a trackpad tap drifts
 *  more — a strict equality would make the scrim refuse to close for some people and not others. */
const DRAG_SLOP_PX = 5;

export function LightboxProvider({ children }: { children: React.ReactNode }) {
  const [filename, setFilename] = useState<string | null>(null);
  /** The listener below is registered once and must see the live value, not a stale closure. */
  const isOpen = useRef(false);
  const restoreFocus = useRef<Element | null>(null);
  const isPhone = usePhone();

  const [natural, setNatural] = useState<Size>({ width: 0, height: 0 });
  const [viewport, setViewport] = useState<Size>({ width: 0, height: 0 });
  const [zoom, setZoom] = useState<ZoomState>({ scale: 1, x: 0, y: 0 });
  /** Set once the image reports its size, so the first paint is not at a guessed scale. */
  const sized = useRef(false);
  const surface = useRef<HTMLDivElement | null>(null);
  /** Where the pointer went down, so the scrim can tell a click from the end of a drag. */
  const pressAt = useRef<{ x: number; y: number } | null>(null);

  const fit = natural.width > 0 ? fitScale(natural, viewport, FIT_PADDING) : 1;

  const apply = useCallback(
    (next: ZoomState) => setZoom(clampPan(next, natural, viewport)),
    [natural, viewport],
  );

  /**
   * Shut it without touching history — what Back itself calls, once the entry is already gone.
   * Every other way of closing goes through `close`, which also consumes that entry.
   *
   * **The zoom is reset here and not only on `open`.** Back-then-reopen would otherwise restore the
   * previous image's scale and offset onto a different picture, which reads as the lightbox being
   * broken rather than as state that outlived its subject.
   */
  const closeQuietly = useCallback(() => {
    isOpen.current = false;
    sized.current = false;
    setFilename(null);
    setNatural({ width: 0, height: 0 });
    setZoom({ scale: 1, x: 0, y: 0 });
    (restoreFocus.current as HTMLElement | null)?.focus?.();
    restoreFocus.current = null;
  }, []);

  const open = useCallback((name: string) => {
    restoreFocus.current = document.activeElement;
    isOpen.current = true;
    sized.current = false;
    setNatural({ width: 0, height: 0 });
    setZoom({ scale: 1, x: 0, y: 0 });
    setFilename(name);
    // A layer, not a location: this adds a history entry without changing the URL, so Back closes
    // the image and a refresh does not restore one.
    overlayApi()?.openOverlay("lightbox");
  }, []);

  const close = useCallback(() => {
    closeQuietly();
    overlayApi()?.closeOverlay("lightbox");
  }, [closeQuietly]);

  // Registered so a Back that jumps several entries can shut this without going through `close`,
  // which would try to consume an entry the browser has already left.
  useEffect(() => overlayApi()?.registerOverlay("lightbox", closeQuietly), [closeQuietly]);

  /** Track the surface's size, so fit is right on an orientation change or a resized window. */
  useEffect(() => {
    const element = surface.current;
    if (!element || filename === null) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      setViewport({ width: rect.width, height: rect.height });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [filename]);

  /**
   * The opening scale — **fit, and only once BOTH sizes are known.**
   *
   * Deliberately not computed inside the image's `onLoad`, which is where it lived first. The
   * surface is measured by the effect above, which runs after paint, while a **cached** image fires
   * `load` on its own schedule — and every image Review opens is cached, because the card behind it
   * has just drawn the same file. Lose that race and `fitScale` is taken against a 0×0 surface,
   * which `FIT_PADDING` turns into a 1×1 box: a sub-pixel scale, latched by `sized`, so the picture
   * opens invisible and never corrects itself. Keyed on both sizes instead, it cannot matter which
   * of the two arrives first.
   */
  useEffect(() => {
    if (sized.current) return;
    if (natural.width <= 0 || viewport.width <= 0) return;
    sized.current = true;
    setZoom({ scale: fitScale(natural, viewport, FIT_PADDING), x: 0, y: 0 });
  }, [natural, viewport]);

  /**
   * Keyboard precedence, which is the hard part. Seven other window keydown listeners exist and
   * this one has to beat all of them while it is open. It wins two different ways:
   *
   * **The four bubble-phase listeners** — Read's main shortcut handler, `AddedPanel`, `NoteDock`,
   * `Cards` — lose on phase alone, whatever order they registered in. `window` is the first node
   * of the capture phase and the last of the bubble phase, so a capture listener here runs before
   * any of them are reached. That part needs no cooperation and cannot be broken by mounting order.
   *
   * **The two other capture-phase listeners** are decided by registration order, since they share
   * both node and phase:
   * - `RadicalPicker` registers only when the picker opens, which is necessarily after this one, so
   *   `stopImmediatePropagation` silences it.
   * - Read's shift tracker registers *before* this one — React flushes child effects before parent
   *   ones, so mounting the provider above `App` does not put it first, as an earlier version of
   *   this comment claimed. It runs, and that is harmless: it only mirrors `event.shiftKey` into a
   *   ref for the paste handler, and reads no keys of its own.
   *
   * **Review is what makes this load-bearing rather than tidy** (日本-350). Space and the arrows
   * grade a card, so a key reaching the reviewer through this overlay would answer a card whose
   * diagram you were reading. `check-review.mjs` asserts it rather than trusting it.
   *
   * `stopImmediatePropagation` rather than `stopPropagation` precisely because same-node listeners
   * would otherwise still run. Keep the dependency list empty of anything unstable: re-running this
   * effect re-registers the listener at the back of the capture queue, which would put it behind an
   * already-open `RadicalPicker`.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isOpen.current) return;
      event.stopImmediatePropagation();
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
      // No blanket preventDefault: F5 and the browser's own keys stay usable.
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [close]);

  /**
   * Wheel zoom — **`addEventListener` with `passive: false`, never React's `onWheel`.**
   *
   * React registers wheel handlers passively, so a `preventDefault` inside `onWheel` is a no-op that
   * logs a console warning and scrolls the page anyway. `surfaces/read/TabStrip.tsx` carries the
   * same note for the same reason, and this file used to carry the *inverse* of it — a comment
   * explaining that there was nothing here to suppress, which was true while there was nothing to
   * interact with and stopped being true with zoom.
   */
  useEffect(() => {
    const element = surface.current;
    if (!element || filename === null) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const pointer = {
        x: event.clientX - (rect.left + rect.width / 2),
        y: event.clientY - (rect.top + rect.height / 2),
      };
      setZoom((current) =>
        clampPan(zoomAbout(current, pointer, wheelFactor(event.deltaY, event.deltaMode), fit), natural, viewport),
      );
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, [filename, fit, natural, viewport]);

  // Stable, so opening the lightbox does not re-render every consumer — and there are two of them
  // per capture in the log.
  const api = useMemo(() => ({ open, close }), [open, close]);

  const zoomTo = (scale: number) => apply({ ...zoom, scale: clampScale(scale, fit) });

  return (
    <Context.Provider value={api}>
      {children}
      {filename !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`画像: ${filename}`}
          data-check="lightbox"
          tabIndex={-1}
          ref={(el) => el?.focus()}
          // **The scrim closes on a CLICK, and a drag is not a click.** See the class docstring for
          // the reversal drag-to-pan forced; `pressAt` below is the whole of how the two are told
          // apart, and it is one rule in one place rather than a guard per gesture.
          onPointerDown={(event) => {
            pressAt.current = { x: event.clientX, y: event.clientY };
          }}
          onClick={(event) => {
            const from = pressAt.current;
            pressAt.current = null;
            // A `click` is synthesised at the nearest common ancestor of the press and the release,
            // so a pan that lets go beside the picture lands on the surface — or on this scrim —
            // rather than on the image, and every child guard misses it. Measuring the distance is
            // what catches all of those at once, including the case where the image is too small to
            // pan at all and the pan handler never ran.
            if (from && Math.hypot(event.clientX - from.x, event.clientY - from.y) > DRAG_SLOP_PX) {
              return;
            }
            close();
          }}
          className="fixed inset-0 flex flex-col overscroll-contain"
          style={{ background: "var(--overlay-scrim)", zIndex: "var(--z-lightbox)" }}
        >
          <CloseRow onClose={close} />
          <Surface
            ref={surface}
            filename={filename}
            zoom={zoom}
            fit={fit}
            natural={natural}
            viewport={viewport}
            onNatural={setNatural}
            onZoom={apply}
          />
          {/* **No toolbar on a phone** — a zoom percentage is not something a thumb can act on, and
              pinch already does what the buttons are for. */}
          {!isPhone && (
            <Toolbar scale={zoom.scale} onZoom={zoomTo} onActual={() => zoomTo(1)} />
          )}
        </div>
      )}
    </Context.Provider>
  );
}

function CloseRow({ onClose }: { onClose: () => void }) {
  return (
    <div className="pointer-events-none absolute right-5 top-[18px] z-10 flex items-center gap-[9px]">
      <span className="font-mono text-meta leading-none text-ink-4">Escで閉じる</span>
      <button
        type="button"
        aria-label="画像を閉じる"
        data-check="lightbox.close"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        className="pointer-events-auto flex h-[34px] w-[34px] items-center justify-center rounded-[var(--r-3)] border border-line-strong text-body text-ink-2"
        style={{ background: "rgba(20,20,20,.9)" }}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * The image and every gesture that acts on it.
 *
 * **Pan tracks `pointermove`/`pointerup` on `window`, not on the image**, and both halves of that
 * matter. `BottomSheet.tsx` establishes the shape; `Session.tsx:409-412` records why the obvious
 * alternative fails — an element-scoped `pointerup` is lost when the target moves out from under
 * the release, which is precisely what a pan does to the thing being dragged.
 *
 * The `moved` flag is what separates a tap from a drag, so a pan that ends over the scrim does not
 * close the overlay.
 */
const Surface = ({
  ref,
  filename,
  zoom,
  fit,
  natural,
  viewport,
  onNatural,
  onZoom,
}: {
  ref: React.RefObject<HTMLDivElement | null>;
  filename: string;
  zoom: ZoomState;
  fit: number;
  natural: Size;
  viewport: Size;
  onNatural: (size: Size) => void;
  onZoom: (next: ZoomState) => void;
}) => {
  const draggable = canPan(zoom, natural, viewport);

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.button !== 0 || !draggable) return;
    event.preventDefault();
    event.stopPropagation();
    const start = { x: event.clientX, y: event.clientY };
    const from = { ...zoom };
    let moved = false;

    const move = (e: PointerEvent) => {
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
      onZoom({ scale: from.scale, x: from.x + dx, y: from.y + dy });
    };
    const up = (e: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      // **The capture flag has to match the one it was added with**, or this never comes off. A
      // stale copy still closes over `moved === true`, so it would go on swallowing *every*
      // `pointerup` in the app — in the capture phase, at `window`, ahead of every drag handler
      // `BottomSheet` and the library rows own.
      window.removeEventListener("pointerup", up, true);
      // A drag never closes, whatever it ends on — but **the scrim's own distance guard is what
      // enforces that**, not this. Stopping the `pointerup` here does not suppress the `click` the
      // browser synthesises afterwards at the nearest common ancestor of the press and the release,
      // so a pan that lets go beside the picture still reached the scrim. A one-shot capture-phase
      // click-swallow installed here does fix that case and only that case: it is never installed
      // when the image is too small to pan, where the same release closes the picture just as
      // surely. One guard on the scrim covers both, so this stays a plain `stopPropagation`.
      if (moved) e.stopPropagation();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, true);
  };

  /**
   * Pinch — the phone's wheel, and the only reason this is on `touch*` rather than on pointers.
   *
   * A two-finger gesture arrives as two pointers that must be correlated, which the Pointer Events
   * API makes you do by hand; `TouchEvent.touches` already is that list. Review reaches for touch
   * over pointers for a different reason and records it (`Session.tsx:409-412`), but the conclusion
   * is the same: on this surface touch is the API that reports what actually happened.
   */
  const pair = useRef<[{ x: number; y: number }, { x: number; y: number }] | null>(null);

  const points = (event: React.TouchEvent) =>
    [
      { x: event.touches[0]!.clientX, y: event.touches[0]!.clientY },
      { x: event.touches[1]!.clientX, y: event.touches[1]!.clientY },
    ] as [{ x: number; y: number }, { x: number; y: number }];

  const onTouchMove = (event: React.TouchEvent) => {
    if (event.touches.length !== 2) {
      pair.current = null;
      return;
    }
    const now = points(event);
    if (pair.current) {
      const rect = ref.current?.getBoundingClientRect();
      const { factor, midpoint } = pinch(pair.current, now);
      if (rect) {
        onZoom(
          zoomAbout(
            zoom,
            { x: midpoint.x - (rect.left + rect.width / 2), y: midpoint.y - (rect.top + rect.height / 2) },
            factor,
            fit,
          ),
        );
      }
    }
    pair.current = now;
  };

  return (
    <div
      ref={ref}
      className="flex min-h-0 flex-1 items-center justify-center overflow-hidden"
      onTouchStart={(event) => {
        pair.current = event.touches.length === 2 ? points(event) : null;
      }}
      onTouchMove={onTouchMove}
      onTouchEnd={() => {
        pair.current = null;
      }}
      onClick={(event) => {
        // The image is an object now, not a dismissal target. Clicks on the scrim still bubble to
        // the overlay, which is what closes.
        if (event.target !== event.currentTarget) event.stopPropagation();
      }}
    >
      <img
        src={mediaUrl(filename)}
        alt=""
        draggable={false}
        data-check="lightbox.image"
        onLoad={(event) => {
          const image = event.currentTarget;
          onNatural({ width: image.naturalWidth, height: image.naturalHeight });
        }}
        onPointerDown={onPointerDown}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => {
          event.stopPropagation();
          onZoom({ ...zoom, scale: toggleScale(zoom.scale, fit) });
        }}
        className="max-w-none select-none rounded-[var(--r-3)]"
        style={{
          // `natural` is only known after `onLoad`, so until then the image draws at its own size
          // and is corrected on the next paint. Sizing from `natural` rather than from CSS is what
          // lets the maths in `lightboxZoom` be about one number.
          width: natural.width > 0 ? natural.width * zoom.scale : undefined,
          height: natural.height > 0 ? natural.height * zoom.scale : undefined,
          transform: `translate(${zoom.x}px, ${zoom.y}px)`,
          cursor: draggable ? "grab" : "default",
          boxShadow: "var(--sh-popover)",
          touchAction: "none",
        }}
      />
    </div>
  );
};

function Toolbar({
  scale,
  onZoom,
  onActual,
}: {
  scale: number;
  onZoom: (scale: number) => void;
  onActual: () => void;
}) {
  const step = (factor: number) => onZoom(scale * factor);
  return (
    <div className="pointer-events-none flex flex-shrink-0 justify-center pb-[34px]">
      <div
        onClick={(event) => event.stopPropagation()}
        className="pointer-events-auto flex items-center gap-2 rounded-[var(--r-3)] border border-line-strong p-1.5"
        style={{ background: "rgba(20,20,20,.94)", boxShadow: "var(--sh-popover)" }}
      >
        <ToolButton label="縮小" check="lightbox.zoom.out" onClick={() => step(1 / 1.25)}>
          −
        </ToolButton>
        <span
          data-check="lightbox.zoom"
          className="w-16 text-center font-mono text-meta leading-none tabular-nums text-ink"
        >
          {zoomLabel(scale)}
        </span>
        <ToolButton label="拡大" check="lightbox.zoom.in" onClick={() => step(1.25)}>
          ＋
        </ToolButton>
        <span className="h-5 w-px flex-shrink-0 bg-line-strong" />
        <button
          type="button"
          data-check="lightbox.actual"
          onClick={onActual}
          className="px-2.5 text-meta font-semibold leading-none text-ink-3 hover:text-ink"
        >
          原寸
        </button>
        <span className="h-5 w-px flex-shrink-0 bg-line-strong" />
        {/* Named because neither gesture has an affordance: there is nothing on screen that looks
            draggable, and a wheel does something different everywhere else in the app. */}
        <span className="px-2.5 font-mono text-meta leading-none text-ink-4">
          ドラッグで移動 · ホイールで拡大
        </span>
      </div>
    </div>
  );
}

function ToolButton({
  label,
  check,
  onClick,
  children,
}: {
  label: string;
  check: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      data-check={check}
      onClick={onClick}
      className="flex h-[30px] w-[34px] items-center justify-center rounded-[var(--r-2)] text-body leading-none text-ink-2 hover:bg-line-soft"
    >
      {children}
    </button>
  );
}
